import { useCallback } from "react";

import type { GuestPageVisibilityMode } from "@/lib/gql/__generated__/graphql";

import { resolveVisibility } from "@/components/features-new/guest-page-visibility";

import type { MagazineAnalyticsSettingFormModelOnSetViewModelType } from "..";
import type { MagazineAnalyticsSettingFormModel } from "../../../type";

type Props = {
	viewModel: MagazineAnalyticsSettingFormModel["ViewModel"];
	onSetViewModel: MagazineAnalyticsSettingFormModelOnSetViewModelType;
	/** 保存済みのマガジンの公開状態（チェックを外したときに戻す値） */
	savedGuestPageVisibility: GuestPageVisibilityMode;
};

export type MagazineAnalyticsSettingFormModelMethodsType = {
	magazineAnalyticsSettingFormModelMethods: {
		onSetGtmAvailable: (input: boolean) => void;
		onSetGtmID: (input: string) => void;
		onSetPixelAvailable: (input: boolean) => void;
		onSetPixelID: (input: string) => void;
		onSetGuestPageUnlisted: (isUnlisted: boolean) => void;
	};
};

type UseMagazineAnalyticsSettingFormModelMethodsType = ({
	viewModel,
	onSetViewModel,
	savedGuestPageVisibility,
}: Props) => MagazineAnalyticsSettingFormModelMethodsType;

export const useMagazineAnalyticsSettingFormModelMethods: UseMagazineAnalyticsSettingFormModelMethodsType = ({
	onSetViewModel,
	savedGuestPageVisibility,
}: Props) => {
	const onSetGtmAvailable = useCallback(
		(input: boolean) => {
			const gtmAvailable = {
				key: "gtmAvailable",
				value: {
					isEdited: true,
					value: input,
				},
			} as const;

			if (input) {
				onSetViewModel([gtmAvailable]);
			} else {
				onSetViewModel([
					gtmAvailable,
					{
						key: "gtmID",
						value: {
							isEdited: true,
							value: "",
						},
					},
				]);
			}
		},
		[onSetViewModel],
	);

	const onSetGtmID = useCallback(
		(input: string) => {
			onSetViewModel([
				{
					key: "gtmID",
					value: {
						isEdited: true,
						value: input,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const onSetPixelAvailable = useCallback(
		(input: boolean) => {
			const pixelAvailable = {
				key: "pixelAvailable",
				value: {
					isEdited: true,
					value: input,
				},
			} as const;

			if (input) {
				onSetViewModel([pixelAvailable]);
			} else {
				onSetViewModel([
					pixelAvailable,
					{
						key: "pixelID",
						value: {
							isEdited: true,
							value: "",
						},
					},
				]);
			}
		},
		[onSetViewModel],
	);

	const onSetPixelID = useCallback(
		(input: string) => {
			onSetViewModel([
				{
					key: "pixelID",
					value: {
						isEdited: true,
						value: input,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	// チェックありは検索に表示しない。外したときは保存済みの値に戻す（未設定を公開で上書きしない）
	const onSetGuestPageUnlisted = useCallback(
		(isUnlisted: boolean) => {
			onSetViewModel([
				{
					key: "guestPageVisibility",
					value: {
						isEdited: true,
						value: resolveVisibility(isUnlisted, savedGuestPageVisibility),
					},
				},
			]);
		},
		[onSetViewModel, savedGuestPageVisibility],
	);

	return {
		magazineAnalyticsSettingFormModelMethods: {
			onSetGtmAvailable, //
			onSetGtmID,
			onSetPixelAvailable,
			onSetPixelID,
			onSetGuestPageUnlisted,
		},
	};
};
